import React, { useCallback } from "react";
import {
  Menu,
  MenuItem,
  Position,
  Spinner,
  Tag,
  Tooltip,
} from "@blueprintjs/core";
import "styled-components/macro";
import { useBoolean } from "../../helpers/useBoolean";
import { StyledPopoverWrapper } from "../../popover-wrapper";
import _ from "lodash";
import { Intent } from "@blueprintjs/core/lib/esm/common/intent";
import { useMeData } from "../../hooks/useMe";
import { UserRole } from "../../store/model-me";
import { STUDENT_TAGS_INFO } from "../../entities/ecp/tags";
import { useStoreActions } from "../../hooks/ep";

const ALL_MENU_ITEMS = ["tag__behind_pace", "tag__missing_in_action"];
const USER_ROLES__UNIVERSAL_ACCESS = [
  UserRole.SAXECAP_DEV,
  UserRole.ADMIN_READ_WRITE,
  UserRole.ADMIN_READ_ONLY,
];
const USER_ROLES__BEHIND_PACE_ONLY = [
  UserRole.ENROLLMENT_COORDINATOR,
  UserRole.ENROLLMENT_MANAGER,
];
const USER_ROLES__MISSING_IN_ACTION_ONLY = [
  UserRole.ACADEMIC_DEPARTMENT_CHAIR,
  UserRole.DEAN_OF_FACULTY,
];

function useAuthorizedMenuItems() {
  const { role } = useMeData();
  if (USER_ROLES__UNIVERSAL_ACCESS.includes(role)) {
    return ALL_MENU_ITEMS;
  } else if (USER_ROLES__BEHIND_PACE_ONLY.includes(role)) {
    return ["tag__behind_pace"];
  } else if (USER_ROLES__MISSING_IN_ACTION_ONLY.includes(role)) {
    return ["tag__missing_in_action"];
  } else {
    return [];
  }
}

export function StudentEmailActionButtonCellRenderer({
  data,
  value,
  crp,
}: {
  data: any;
  value: string;
  crp: { tooltipText?: string };
}) {
  const authorizedMenuItems = useAuthorizedMenuItems();
  const isLoading = useBoolean(false);
  const handleFetchMailtoLink = useStoreActions(
    (a) => a.misc.handleFetchMailtoLink
  );

  const studentIndex = data["StudentIndex"];

  const enabledMenuItems = ALL_MENU_ITEMS.filter((tagField) => data[tagField]);
  const isMenuItemDisabled = (menuItemId) =>
    !authorizedMenuItems.includes(menuItemId) ||
    !enabledMenuItems.includes(menuItemId);
  const isPopoverTargetDisabled =
    _.size(enabledMenuItems) === 0 || isLoading.value;

  const onClickFactory = useCallback(
    (emailTemplateId) => async () => {
      if (isPopoverTargetDisabled) {
        return;
      }
      isLoading.setTrue();
      await handleFetchMailtoLink({
        entityType: "student",
        entityId: studentIndex,
        emailTemplateId,
      });
      isLoading.setFalse();
    },
    // eslint-disable-next-line
    [
      studentIndex,
      isPopoverTargetDisabled,
      isLoading.setTrue,
      isLoading.setFalse,
    ]
  );

  const BehindPaceIcon = STUDENT_TAGS_INFO.tagIcons.behind_pace;
  const MissingInActionIcon = STUDENT_TAGS_INFO.tagIcons.missing_in_action;

  const popoverContentMenu = (
    <Menu large>
      <MenuItem
        intent={Intent.WARNING}
        icon={<BehindPaceIcon />}
        text={STUDENT_TAGS_INFO.tagDisplayNames.behind_pace}
        onClick={onClickFactory("tag__behind_pace")}
        shouldDismissPopover={true}
        disabled={isMenuItemDisabled("tag__behind_pace")}
      />
      <MenuItem
        intent={Intent.WARNING}
        icon={<MissingInActionIcon />}
        text={STUDENT_TAGS_INFO.tagDisplayNames.missing_in_action}
        onClick={onClickFactory("tag__missing_in_action")}
        shouldDismissPopover={true}
        disabled={isMenuItemDisabled("tag__missing_in_action")}
      />
    </Menu>
  );

  return (
    <span>
      <StyledPopoverWrapper
        canEscapeKeyClose
        content={popoverContentMenu}
        position={Position.RIGHT_BOTTOM}
        usePortal={false}
        hoverCloseDelay={0}
        isOpen={isLoading.value ? false : null}
      >
        <Tooltip
          content={"Compose email from template"}
          boundary="viewport"
          hoverOpenDelay={1500}
          hoverCloseDelay={0}
          isOpen={isLoading.value ? false : null}
        >
          <Tag
            css={`
              font-size: 12px;
              height: 26px;
              & .bp3-icon,
              & .bp3-spinner {
                margin-right: 6px;
              }
            `}
            fill={true}
            minimal={true}
            intent={isPopoverTargetDisabled ? "none" : "primary"}
            large={false}
            icon={
              isLoading.value ? (
                <Spinner size={Spinner.SIZE_SMALL} />
              ) : (
                "envelope"
              )
            }
            interactive={!isPopoverTargetDisabled}
            disabled={isPopoverTargetDisabled}
          />
        </Tooltip>
      </StyledPopoverWrapper>
    </span>
  );
}
